/**
 * "Decide in Escalations" from the in-thread question card (agion/Agion#3446)
 * opens this chat's own Escalations section, never the admin console: the
 * link's target is the section's anchor, and its click asks the layout to
 * show the sidebar (the phone drawer, or an expanded sidebar) and the section
 * to scroll into view.
 */

import { useEffect } from 'react'

/** The Escalations section's element id, in the current and the new chat. */
export const ESCALATIONS_SECTION_ID = 'escalations'

/** The card's link target. */
export const ESCALATIONS_SECTION_HREF = `#${ESCALATIONS_SECTION_ID}`

export const REVEAL_ESCALATIONS_EVENT = 'agion:reveal-escalations'

/** Asks the layout to show the sidebar and the Escalations section to come into view. */
export function revealEscalations(): void {
  window.dispatchEvent(new Event(REVEAL_ESCALATIONS_EVENT))
}

/** Calls onReveal whenever the Escalations section is asked for. */
export function useOnRevealEscalations(onReveal: () => void): void {
  useEffect(() => {
    window.addEventListener(REVEAL_ESCALATIONS_EVENT, onReveal)
    return () => window.removeEventListener(REVEAL_ESCALATIONS_EVENT, onReveal)
  }, [onReveal])
}

function scrollSectionIntoView(): void {
  // After the layout has drawn the sidebar it was asked to show.
  window.requestAnimationFrame(() => {
    document.getElementById(ESCALATIONS_SECTION_ID)?.scrollIntoView?.({ block: 'start', behavior: 'smooth' })
  })
}

/**
 * For the section itself: scrolls into view when asked, and when it mounts
 * while the address names it (a drawer that opened for the link mounts the
 * section after the request).
 */
export function useEscalationsSectionAnchor(): void {
  useOnRevealEscalations(scrollSectionIntoView)
  useEffect(() => {
    if (window.location.hash === ESCALATIONS_SECTION_HREF) scrollSectionIntoView()
  }, [])
}
